<template>
  <div class="p-3">
    <div class="d-flex align-items-center mb-3">
      <b-form-input v-model="search" placeholder="Αναζήτηση κλειδιού ή κειμένου..." class="mr-3" />
      <b-button variant="success" @click="saveChanges" :disabled="saving">
        {{ saving ? 'Αποθήκευση...' : 'Αποθήκευση' }}
      </b-button>
    </div>

    <div v-if="loaded" style="max-height:600px;overflow-y:auto;">
      <table class="table table-sm table-bordered">
        <thead>
          <tr>
            <th style="width:30%">Κλειδί</th>
            <th style="width:35%">Προεπιλογή</th>
            <th style="width:35%">Τιμή</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredKeys" :key="item.key">
            <td class="text-muted" style="font-size:12px;vertical-align:middle;">{{ item.key }}</td>
            <td style="font-size:13px;vertical-align:middle;color:#666;">{{ item.defaultValue }}</td>
            <td>
              <b-form-input
                v-model="overrides[item.key]"
                :placeholder="item.defaultValue"
                size="sm"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="text-center py-4">Φόρτωση...</div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loaded: false,
      saving: false,
      search: '',
      defaultKeys: [],
      overrides: {},
    }
  },
  computed: {
    filteredKeys() {
      if (!this.search) return this.defaultKeys;
      const q = this.search.toLowerCase();
      return this.defaultKeys.filter(item =>
        item.key.toLowerCase().includes(q) ||
        item.defaultValue.toLowerCase().includes(q) ||
        (this.overrides[item.key] || '').toLowerCase().includes(q)
      );
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      try {
        const [, overridesRes] = await Promise.all([
          this.axios.get('/general/translations?defaults=true'),
          this.axios.get('/general/translations'),
        ]);

        // Build overrides from saved data
        const saved = overridesRes.data || {};
        this.overrides = {};
        this.flattenObject(saved, '', this.overrides);

        // Build default keys from hardcoded defaults
        this.defaultKeys = this.buildDefaultKeys();

        this.loaded = true;
      } catch (e) {
        this.$notify({ type: 'error', title: 'Σφάλμα', text: 'Δεν ήταν δυνατή η φόρτωση.' });
      }
    },

    buildDefaultKeys() {
      // Hardcoded defaults matching the frontend lang/el files
      const defaults = {
        'auth.login.title': 'Σύνδεση',
        'auth.login.subtitle': 'Συνδεθείτε με τον λογαριασμό σας για να δείτε το ιστορικό των παραγγελιών σας, να αποθηκεύετε διευθύνσεις κ.λπ.',
        'auth.forgotPassword': 'Ξέχασα τον κωδικό μου',
        'auth.password': 'Κωδικός πρόσβασης',
        'auth.repeatPassword': 'Επανάληψη κωδικού πρόσβασης',
        'auth.noAccount': 'Δεν έχετε λογαριασμό;',
        'auth.goRegister': 'Κάντε τώρα εγγραφή κάνοντας κλικ εδώ',
        'auth.noOrders': 'Δεν έχετε κάνει ακόμα καμία παραγγελία!',
        'auth.register.title': 'Εγγραφή',
        'auth.register.subtitle': 'Εγγραφείτε για να μπορείτε να αποθηκεύσετε την διεύθυνσή σας, το ιστορικό των παραγγελιών σας και τις ρυθμίσεις σας',
        'auth.account.title': 'Ο λογαριασμός μου',
        'auth.account.welcome': 'Καλωσόρισες',
        'auth.reset.title': 'Επαναφορά κωδικού πρόσβασης',
        'auth.reset.button': 'Επαναφορα κωδικού πρόσβασης',
        'auth.payment.title': 'Μέθοδος πληρωμής',
        'auth.payment.subtitle': 'Δείτε την αποθηκευμένη σας πιστωτική / χρεωστική κάρτα ή αποθηκεύσετε μία νέα.',
        'auth.progress.addMeasurement': 'Προσθήκη μέτρησης',
        'auth.progress.weightProgressDiagram': 'Διάγραμμα προόδου',

        'checkout.personal_info.title': 'Προσωπικές πληροφορίες',
        'checkout.shipping_address.title': 'Διεύθυνση αποστολής',
        'checkout.firstName': 'Όνομα',
        'checkout.lastName': 'Επώνυμο',
        'checkout.email': 'E-mail',
        'checkout.phone': 'Τηλ. Επικοινωνίας',
        'checkout.address': 'Διεύθυνση',
        'checkout.city': 'Πόλη',
        'checkout.zipCode': 'Ταχυδρομικός κώδικας',
        'checkout.door': 'Όροφος / Όνομα στο κουδούνι',
        'checkout.notes': 'Σημειώσεις',
        'checkout.time': 'Επιλογή διάστηματος παράδοσης',
        'checkout.delivery': 'Αποστολή στην παραπάνω διεύθυνση',
        'checkout.takeaway': 'Παραλαβή από το κατάστημα της Courier',
        'checkout.weight': 'Βάρος',
        'checkout.height': 'Ύψος',
        'checkout.age': 'Ηλικία',
        'checkout.gender': 'Φύλο',
        'checkout.activity': 'Επίπεδο Δραστηριότητας',

        'cart.coupon.title': 'Έχετε κουπόνι έκπτωσης;',
        'cart.coupon.placeholder': 'Εισάγετε τον κωδικό κουπονιού σας',
        'cart.coupon.invalid': 'Το κουπόνι δεν είναι έγκυρο',
        'cart.deliveryFee': 'Κόστος αποστολής',
        'cart.paymentFee': 'Κόστος πληρωμής',

        'order.shipping.title': 'Τρόποι αποστολής',
        'order.shipping.courier': 'Αποστολή με εταιρεία Courier Γενική Ταχυδρομική',
        'order.shipping.courier_desc': 'Παραλάβετε στην πόρτα σας με εταιρεία ταχυμεταφορών',
        'order.shipping.courier2': 'Αποστολή με εταιρεία Courier ACS Courier',
        'order.shipping.courier2_desc': 'Παραλάβετε στην πόρτα σας με εταιρεία ταχυμεταφορών',
        'order.shipping.courier3': 'Αποστολή με εταιρεία Courier',
        'order.shipping.courier3_desc': 'Παραλάβετε στην πόρτα σας με εταιρεία ταχυμεταφορών',
        'order.shipping.store_pickup': 'Παραλαβή από το κατάστημα',
        'order.shipping.store_pickup_desc': 'Παραλάβετε από το φυσικό μας κατάστημα Μητροπόλεως 7 Ιωάννινα',
        'order.payment_methods.title': 'Τρόποι πληρωμής',
        'order.payment_methods.card': 'Πληρωμή με πιστωτική / χρεωστική κάρτα',
        'order.payment_methods.card_desc': 'Με ασφάλεια στο σύστημά μας μέσω της Stripe.',
        'order.payment_methods.bank_transfer': 'Τραπεζική μεταφορά',
        'order.payment_methods.bank_transfer_desc': 'Πληρωμή με τραπεζική μεταφορά',
        'order.payment_methods.cod': 'Αντικαταβολή',
        'order.payment_methods.cod_desc': 'Πληρωμή στην πόρτα σας στην εταιρεία μεταφοράς',
        'order.sendRegular': 'Αποστολή Παραγγελίας',
        'order.sendCard': 'Αποστολή & Πληρωμή Παραγγελίας',

        'general.apply': 'Εφαρμογή',
        'general.filters': 'Φίλτρα',
        'general.close': 'Κλείσιμο',
        'general.cancel': 'Ακύρωση',
        'general.delete': 'Διαγραφή',
        'general.create': 'Δημιουργία',

        'meals.order_types.WEEKLY.title': 'Πλάνο Διατροφής',
        'meals.order_types.FREE.title': 'Ελεύθερο Εβδομαδιαίο Πλάνο',
        'meals.order_types.A_LA_CARTE.title': 'A La Carte',

        'errors.somethingWentWrong': 'Κάτι πήγε λάθος. Παρακαλώ προσπαθήστε ξανά!',
        'errors.failedPayment': 'Η πληρωμή απέτυχε. Παρακαλώ δοκιμάστε ξανά.',

        'general.yes': 'Ναι',
        'general.no': 'Όχι',
        'general.units': 'μονάδες',
        'general.unitsInfo': '* Μ: Γευματικό Ισοδύναμο: μ.ο. 120 θερμίδες',
        'general.nutritionInfo': 'P: Πρωτείνες, C: Yδατάνθρακες, F: Λίπη Kcal: Θερμίδες',
        'general.previousStep': 'Προηγούμενο βήμα',
        'general.nextStep': 'Επόμενο βήμα',
        'general.saveChanges': 'Αποθήκευση αλλαγών',
        'general.noResultsForFilters': 'Δεν βρέθηκαν προϊόντα που να ταιριάζουν με τα φίλτρα σας.',

        'auth.settings.personalDescription': 'Εδώ μπορείτε να επεξεργαστείτε τα στοιχεία του λογαριασμού σας.',
        'auth.payment.changeCard': 'Αλλαγή κάρτας',
        'auth.payment.cardReplaceWarning': 'Προσοχή. Η κάρτα σας θα αντικατασταθεί από την τωρινή.',
        'auth.payment.saveCardSecurely': 'Αποθηκεύστε την κάρτα σας με ασφάλεια.',
        'auth.payment.saveCard': 'Αποθήκευση κάρτας',
        'auth.payment.noCardSaved': 'Δεν έχετε αποθηκεύσει καμία κάρτα!',

        'checkout.is_ioannina': 'Βρίσκεστε στα Ιωάννινα;',

        'order.loginRequired': 'Για να προχωρήσετε, πρέπει να δημιουργήσετε ένα λογαριασμό ή να συνδεθείτε!',
        'order.deliveryDate': 'Ημερομηνία αποστολής',
        'order.cycle': 'Κύκλος',
        'order.total': 'Σύνολο',
        'order.paymentMethod': 'Τρόπος πληρωμής',
        'order.breakdown': 'Ανάλυση παραγγελίας',
        'order.noFoodsSelected': 'Δεν έχετε επιλέξει τρόφιμα για αυτή την εβδομάδα!',
        'order.selectFoodsLink': 'Πατήστε εδώ για να επιλέξετε τα τρόφιμα',
        'order.completePayment': 'Ολοκλήρωση πληρωμής',
        'order.editProducts': 'Επεξεργασία/προβολή προϊόντων',
        'order.viewProducts': 'Προβολή προϊόντων',
        'order.changeAddress': 'Αλλαγή διεύθυνσης αποστολής',
        'order.cancelOrder': 'Ακύρωση αποστολής',
        'order.confirmCancelOrder': 'Θέλετε σίγουρα να ακυρώσετε αυτή την αποστολή;',
        'order.shippingDetails': 'Στοιχεία αποστολής',
        'order.sendToIoannina': 'Αποστολή στα Ιωάννινα',
        'order.cannotEditOrder': 'Δεν μπορείτε να επεξεργαστείτε τα περιεχόμενα της παραγγελίας σας γιατί έχουν κλειδώσει οι αλλαγές ή έχετε ήδη ολοκληρώσει την πληρωμή.',
        'order.newTotal': 'Νέο σύνολο παραγγελίας',
        'order.minimumFoodsRequired': 'Δεν μπορείτε να διαλέξετε λιγότερα τρόφιμα από τα απαραίτητα της συνδρομής σας.',
        'order.confirmExtraMeal': 'Θέλετε σίγουρα να προσθέσετε ένα έξτρα γεύμα; Αυτό το γεύμα είναι εκτός του πλάνου σας, και έχει πρόσθετη χρέωση.',
        'order.extraMeal': 'EXTRA ΓΕΥΜΑ',
        'order.noProductsAdded': 'Δεν έχετε προσθέσει ακόμα προϊόντα',
        'order.totalOrder': 'Σύνολο παραγγελίας',
        'order.selectMeals': 'Επιλέξτε από τα εικονιζόμενα γεύματα',
        'order.selectMealStructure': 'Επιλέξτε τη δομή των γευμάτων σας',
        'order.selectQuantity': 'Επιλέξτε ποσότητα ανά γεύμα / μέγεθος',
        'order.selectQuantityShort': 'Επιλέξτε ποσότητα',
        'order.categoryQuestion': 'Ανήκετε σε κάποια απο τις παρακάτω κατηγορίες;',
        'order.selectDays': 'Πόσες ημέρες γευμάτων επιθυμείτε;',
        'order.saltFreeQuestion': 'Θέλετε τα γεύματα να είναι ανάλατα;',
        'order.selectDeliveryDate': 'Επιλέξτε ημερομηνία παράδοσης',
        'order.overviewTitle': 'Σύνοψη παραγγελίας',
        'order.type': 'Είδος',
        'order.typePlan': 'Πλάνο διατροφής',
        'order.typeFree': 'Ελεύθερο εβδομαδιαίο πλάνο',
        'order.typeAlaCarte': 'A la carte',
        'order.addressChangeNote': '* Η αλλαγή διεύθυνσης ισχύει μόνο για αυτή την παράδοση!',
        'order.completeTransaction': 'Ολοκλήρωση συναλλαγής',
        'order.useStoredCard': 'Για την συναλλαγή σας θα χρησιμοποιηθεί η παρακάτω αποθηκευμένη πιστωτική / χρεωστική σας κάρτα.',
        'order.newCardPayment': 'Ολοκλήρωση πληρωμής με νέα πιστωτική / χρεωστική κάρτα',
        'order.editSubscription': 'Επεξεργασία συνδρομής',
        'order.subscriptionNameHelp': 'Μπορείτε να επεξεργαστείτε την ονομασία της συνδρομής σας για να την αναγνωρίζετε ευκολότερα!',
        'order.subscriptionName': 'Όνομα (προαιρετικό)',
        'order.subscriptionNote': 'Σημείωση (προαιρετικό)',
        'order.cancelSubscription': 'Ακύρωση συνδρομής',
        'order.cancelSubscriptionConfirm': 'Θέλετε σίγουρα να ακυρώσετε τη συνδρομή σας;',
        'order.activeSubscriptions': 'Οι ενεργές συνδρομές σας',
        'order.noActiveSubscription': 'Δεν έχετε ενεργή συνδρομή.',
        'order.activeTab': 'Ενεργές συνδρομές',
        'order.cancelledTab': 'Ακυρωμένες συνδρομές',
        'order.cancelledLabel': 'ΑΚΥΡΩΜΕΝΗ ΣΥΝΔΡΟΜΗ',
        'order.noSubscriptionsFound': 'Δεν βρέθηκαν συνδρομές.',

        'menu.nutritionGuide': 'Οδηγός διατροφής',
        'menu.guideDescription': 'Οδηγίες από τον/τη διατροφολόγο σας.',
        'menu.guideNotFound': 'Δεν βρέθηκαν σημειώσεις.',
        'menu.guideCheckLater': 'Εάν έχετε ξεκινήσει τη συνδρομή σας, ελέγξτε ξανά σύντομα!',

        'sidebar.login': 'Σύνδεση',
        'sidebar.register': 'Εγγραφή',

        // Root
        'update': 'Αποθήκευση αλλαγών',
        'select': 'Επιλογή',

        // Auth - addresses
        'auth.passwordPlaceholder': '*******',
        'auth.addresses.edit': 'Επεξεργασία',
        'auth.addresses.create': 'Δημιουργία νέας',
        'auth.addresses.update': 'Αποθήκευση αλλαγών',
        'auth.addresses.add': 'Προσθήκη διεύθυνσης',
        'auth.reset.subtitle': 'Επαναφέρετε τον κωδικό πρόσβασής σας μέσω e-mail',
        'auth.reset.hasGoogle': 'Δεν μπορείτε να επαναφέρετε τον κωδικό γιατί χρησιμοποιείτε σύνδεση με google.',
        'auth.reset.success': 'Έχει σταλεί ένας σύνδεσμος επαναφοράς στο e-mail σας!',
        'auth.changeReset.title': 'Δημιουργία νέου κωδικού πρόσβασης',
        'auth.changeReset.subtitle': 'Εισάγετε νέο κωδικό πρόσβασης για τον λογαριασμό σας!',
        'auth.changeReset.button': 'Αλλαγή κωδικού πρόσβασης',
        'auth.settings.personal': 'Προσωπικά στοιχεία',
        'auth.progress.measurementList': 'Ιστορικό μετρήσεων',
        'auth.progress.weight': 'Βάρος',
        'auth.progress.height': 'Ύψος',
        'auth.progress.birthDate': 'Ημερομηνία Γέννησης',
        'auth.progress.heightPlaceholder': 'Το ύψος σας σε εκατοστά (π.χ. για 1,80μ γράφετε 180)',
        'auth.progress.birthDatePlaceholder': 'H ημερομηνία γέννησής σας',
        'auth.progress.measurementDate': 'Ημερομηνία μέτρησης',
        'auth.progress.weightPlaceholder': 'Το βάρος σας σε kg',
        'auth.progress.notes': 'Σημειώσεις',
        'auth.progress.notesPlaceholder': 'Κάποιες σημειώσεις για τη μέτρησή σας',

        // Cart
        'cart.addToCart': 'Προσθήκη στο καλάθι',
        'cart.quantity': 'Ποσότητα',
        'cart.close': 'Κλείσιμο',
        'cart.title': 'Καλάθι αγορών',
        'cart.complete': 'Ολοκλήρωση παραγγελίας',
        'cart.subtotal': 'Υπό σύνολο',
        'cart.discounts': 'Εκπτώσεις',
        'cart.remove': 'Αφαίρεση',
        'cart.total': 'Σύνολο',
        'cart.outOfStock': 'Το προϊόν αυτό έχει εξαντληθεί.',
        'cart.empty': 'Δεν έχετε προσθέσει ακόμα προϊόντα στο καλάθι σας!',

        // Checkout - missing placeholders and fields
        'checkout.personal_weight_info.title': 'Σωματομετρικές πληροφορίες',
        'checkout.firstNamePlaceholder': 'Το όνομά σας',
        'checkout.lastNamePlaceholder': 'Το επώνυμό σας',
        'checkout.emailPlaceholder': 'Το e-mail σας',
        'checkout.phonePlaceholder': 'Το τηλέφωνό σας',
        'checkout.addressPlaceholder': 'Διεύθυνση',
        'checkout.country': 'Χώρα',
        'checkout.name': 'Όνομα παραλήπτη',
        'checkout.cityPlaceholder': 'π.χ. Αθήνα',
        'checkout.zipCodePlaceholder': 'π.χ. 14452',
        'checkout.doorPlaceholder': 'Π.χ. 1ος / Γιάννης',
        'checkout.notesPlaceholder': 'Σημειώσεις σχετικά με την παράδοση της αποστολής.',
        'checkout.issueInvoice': 'Έκδοση τιμολογίου;',
        'checkout.vat': 'Α.Φ.Μ. (προαιρετικό)',
        'checkout.vatPlaceholder': 'Μόνο σε περίπτωση τιμολογίου',
        'checkout.doy': 'ΔΟΥ',
        'checkout.doyPlaceholder': 'π.χ. Νέας Ιωνίας',
        'checkout.companyAddress': 'Διεύθυνση εταιρείας',
        'checkout.companyAddressNote': 'όπως θέλετε να αναγράφεται στο τιμολόγιο',
        'checkout.companyName': 'Επωνυμία εταιρείας',
        'checkout.companyNamePlaceholder': 'π.χ. DreamDiet',
        'checkout.newAddress': 'Εισαγωγή άλλης διεύθυνσης',
        'checkout.weightPlaceholder': 'π.χ. 70',
        'checkout.heightPlaceholder': 'π.χ. 180 για 1,80m',
        'checkout.agePlaceholder': 'π.χ. 30',
        'checkout.activityPlaceholder': 'Ποια είναι η καθημερινή σας δραστηριότητα;',

        // Countries
        'countries.GR': 'Ελλάδα',
        'countries.CY': 'Κύπρος',

        // Errors
        'errors.out_of_stock': 'Η παραγγελία απέτυχε γιατί κάποια προϊόντα δεν έχουν διαθέσιμο απόθεμα.',
        'errors.1': 'Κάτι πήγε λάθος. Παρακαλώ δοκιμάστε ξανά!',
        'errors.4': 'Τα πεδία που συμπληρώσατε είναι εσφαλμένα.',
        'errors.5': 'Υπάρχει ήδη λογαριασμός με αυτό το e-mail.',
        'errors.password_not_match': 'Οι κωδικοί πρόσβασης δεν ταιριάζουν.',
        'errors.authenticationFailed': 'Η ταυτοποίηση απέτυχε. Παρακαλώ δοκιμάστε ξανά.',

        // General - menu and footer
        'general.menu.home': 'Αρχική',
        'general.menu.shop': 'Προϊόντα',
        'general.menu.about': 'Για εμάς',
        'general.menu.blog': 'Blog',
        'general.footerMenu.customerSupport': 'Εξυπηρέτηση πελατών',
        'general.footerMenu.trackOrder': 'Παρακολούθηση αποστολής',
        'general.footerMenu.paymentMethods': 'Τρόποι πληρωμής',
        'general.footerMenu.shippingMethods': 'Τρόποι αποστολής',
        'general.footerMenu.transactionSafety': 'Ασφάλεια συναλλαγών',
        'general.footerMenu.information': 'Πληροφορίες',
        'general.footerMenu.contact': 'Επικοινωνία',
        'general.footerMenu.terms': 'Όροι χρήσης',
        'general.footerMenu.privacy': 'Προσωπικά δεδομένα',
        'general.success': 'Τα στοιχεία ενημερώθηκαν επιτυχώς.',
        'general.successCreation': 'Η δημιουργία ολοκληρώθηκε επιτυχώς',
        'general.sizes.small': 'Small',
        'general.sizes.regular': 'Regular',
        'general.sizes.large': 'Large',
        'general.selectFilter': 'Κάντε κλικ στα φίλτρα παρακάτω για να προσαρμόσετε τα προϊόντα που βλέπετε σε κάθε κατηγορία με βάση τις διατροφικές σας προτιμήσεις!',
        'general.unitsShort': 'Μ',

        // Meals
        'meals.BREAKFAST': 'Πρωϊνό',
        'meals.LUNCH': 'Μεσημεριανό',
        'meals.DINNER': 'Βραδινό',
        'meals.SNACK': 'Ενδιάμεσο Σνακ',
        'meals.GRAB_GO_DINNER': 'Grab & Go Βραδινό',
        'meals.DESERT': 'Επιδόρπιο',
        'meals.add': 'Προσθήκη στη λίστα',
        'meals.mealsSizes.breakfast.null': 'Χωρίς πρωινό',
        'meals.mealsSizes.breakfast.regular': 'Single',
        'meals.mealsSizes.breakfast.large': 'Double',
        'meals.mealsSizes.snack.null': 'Χωρίς σνακ',
        'meals.mealsSizes.snack.regular': 'Με σνακ',
        'meals.mealsSizes.lunch.small': 'Small',
        'meals.mealsSizes.lunch.regular': 'Regular',
        'meals.mealsSizes.lunch.large': 'Large',
        'meals.mealsSizes.dinner.small': 'Small',
        'meals.mealsSizes.dinner.regular': 'Regular',
        'meals.mealsSizes.dinner.large': 'Large',
        'meals.mealsSizes.desert.null': 'Χωρίς επιδόρπιο',
        'meals.mealsSizes.desert.regular': 'Με επιδόρπιο',
        'meals.steps.0.title': 'Είδος παραγγελίας',
        'meals.steps.1.title': 'Επιλογές πλάνου',
        'meals.steps.2.title': 'Επιλογή γευμάτων',
        'meals.steps.3.title': 'Ολοκλήρωση παραγγελίας',
        'meals.order_types.select': 'Ποιο είδος παραγγελίας ταιριάζει στο στόχο σας;',
        'meals.order_types.popular': 'Δημοφιλής επιλογή',
        'meals.order_types.A_LA_CARTE.short': 'Α LA CARTE',
        'meals.order_types.A_LA_CARTE.description': 'Παραγγείλετε μια φορά τα γεύματα της αρεσκείας σας, χωρίς συνδρομή και περιορισμούς.',
        'meals.order_types.FREE.short': 'YOUR CHOICE',
        'meals.order_types.FREE.description': 'Γνωρίζετε οι ίδιοι τις ανάγκες σας; Με το αποκλειστικό μας πρόγραμμα YOUR CΗΟΙCE και την κωδικοποίηση της Dream Diet, οργανώσετε εύκολα το προσωπικό σας στυλ διατροφής και ξενοιάστε!',
        'meals.order_types.WEEKLY.short': 'WEIGHT LOSS',
        'meals.order_types.WEEKLY.description': 'Μην μετράτε θερμίδες… Χάστε εύκολα κιλά με τη κωδικοποιημένη και αποτελεσματική μέθοδο των γευματικά ισοδύναμων μονάδων (Μ) της Dream Diet',

        // Order - summaries and statuses
        'order.order': 'Παραγγελία',
        'order.date': 'Ημερομηνία',
        'order.summary.PROCESSING.title': 'Η παραγγελία σας είναι σε επεξεργασία',
        'order.summary.SHIPPING.title': 'Η παραγγελία σας έχει αποσταλλεί',
        'order.summary.COMPLETED.title': 'Η παραγγελία σας έχει ολοκληρωθεί!',
        'order.summary.CANCELLED.title': 'Η παραγγελία σας έχει ακυρωθεί!',
        'order.summary.DRAFT.title': 'Πρόχειρη',
        'order.summary.PENDING_PAYMENT.title': 'Αναμένεται πληρωμή',
        'order.summary.SUBSCRIPTION_CANCELLED.title': 'Ακυρωμένη συνδρομή',
        'order.status': 'Κατάσταση',
        'order.num': 'Αρ. Παραγγελίας',
        'order.statuses.PROCESSING': 'Σε επεξεργασία',
        'order.statuses.SHIPPING': 'Στην μεταφορική',
        'order.statuses.PENDING_PAYMENT': 'Αναμένεται πληρωμή',
        'order.statuses.CANCELLED': 'Ακυρωμένη',
        'order.statuses.COMPLETED': 'Ολοκληρωμένη',
        'order.genders.M': 'Άρρεν',
        'order.genders.F': 'Θήλυ',
        'order.payment.pay': 'Πληρωμή παραγγελίας',
        'order.payment.payBtn': 'Πληρωμή',
        'order.payment.loading': 'Φορτώνει...',
        'order.payment.processing': 'Γίνεται επεξεργασία της πληρωμής...',
        'order.payment_methods.pay_on_receive': 'Πληρωμή στο κατάστημα',
        'order.payment_methods.pay_on_receive_desc': 'Πληρώστε στο κατάστημα κατά την παραλαβή',
        'order.availableSizes': 'Διαθέσιμο σε μεγέθη',
        'order.subscriptionNamePlaceholder': 'Εισάγετε ένα όνομα για τη συνδρομή σας (π.χ. Νηστίσιμη)',
        'order.subscriptionNotePlaceholder': 'Εισάγετε κάποια σημείωση για τη συνδρομή σας.',
        'order.cancelSubscriptionPaidNote': 'Εάν κάποια παραγγελία σας έχει πληρωθεί, δεν θα ακυρωθεί. Θα ακυρωθούν μόνο οι επερχόμενες παραγγελίες.',
        'order.subscriptionPrefix': 'Συνδρομή',

        // Sidebar navigation
        'sidebar.members': 'Μέλη / Διευθύνσεις',
        'sidebar.account': 'Λογαριασμός',
        'sidebar.orders': 'Συνδρομές',
        'sidebar.progress': 'Μετρήσεις / Πρόοδος',
        'sidebar.logout': 'Αποσύνδεση',
        'sidebar.newOrder': 'Νέα παραγγελία',
        'sidebar.payment': 'Μέθοδος πληρωμής',
        'sidebar.guide': 'Οδηγός διατροφής',
      };

      return Object.entries(defaults).map(([key, value]) => ({
        key,
        defaultValue: value
      }));
    },

    flattenObject(obj, prefix, result) {
      for (const key of Object.keys(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          this.flattenObject(obj[key], fullKey, result);
        } else {
          result[fullKey] = obj[key];
        }
      }
    },

    unflattenObject(flat) {
      const result = {};
      for (const [key, value] of Object.entries(flat)) {
        if (!value || value === '') continue;
        const parts = key.split('.');
        let current = result;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
      }
      return result;
    },

    saveChanges() {
      this.saving = true;
      const nested = this.unflattenObject(this.overrides);
      this.axios.put('/general/translations', { translations: nested }).then(() => {
        this.$notify({ type: 'success', title: 'Επιτυχία', text: 'Οι μεταφράσεις αποθηκεύτηκαν.' });
      }).catch(() => {
        this.$notify({ type: 'error', title: 'Σφάλμα', text: 'Δεν ήταν δυνατή η αποθήκευση.' });
      }).finally(() => {
        this.saving = false;
      });
    }
  }
}
</script>
